/* eslint-disable max-lines */
/** Where a credit lot may be redeemed. Distinct from which product it was earned on. */
export enum CreditRedeemableScope {
  Any = 0,
  EsimOnly = 1,
  TravelOnly = 2
}

export const REDEEMABLE_SCOPE_LABELS: Record<CreditRedeemableScope, string> = {
  [CreditRedeemableScope.Any]: "Bileta dhe eSIM",
  [CreditRedeemableScope.EsimOnly]: "Vetëm eSIM",
  [CreditRedeemableScope.TravelOnly]: "Vetëm bileta"
};

// The API sends enum *names* on ledger rows, not numbers. Everything below maps them to the same
// Albanian wording the rest of the back office uses — an agent reading a customer's history should not
// have to know that "EarnedFromDailyChallenge" is a thing.
export const SCOPE_NAME_LABELS: Record<string, string> = {
  Any: "Bileta dhe eSIM",
  EsimOnly: "Vetëm eSIM",
  TravelOnly: "Vetëm bileta"
};

export const PRODUCT_NAME_LABELS: Record<string, string> = {
  Unspecified: "-",
  Esim: "eSIM",
  Travel: "Bileta"
};

export const CREDIT_TYPE_LABELS: Record<string, string> = {
  EarnedFromPurchase: "Fitoi nga një blerje eSIM",
  SpentOnPurchase: "Pagoi me kredite",
  Expired: "Skaduan pa u përdorur",
  ManualAdjustment: "Rregullim me dorë",
  ReversedDueToCancellation: "U hoqën pas anulimit",
  EarnedFromReferral: "Fitoi duke referuar një shok",
  EarnedFromWelcome: "Dhuratë mirëseardhje",
  EarnedFromExternalTransfer: "Erdhën nga një transfer",
  EarnedFromVoucherRedemption: "Fitoi nga një kupon",
  EarnedFromDailyChallenge: "Fitoi nga sfida ditore",
  EarnedFromStreak: "Fitoi nga seria e ditëve",
  EarnedFromSkyPilotTurbo: "Fitoi nga loja SkyPilot",
  EarnedFromLeaderboard: "Fitoi nga renditja",
  RefundedToCredits: "Paratë u kthyen si kredite",
  SpentCreditsReturned: "Kreditet u kthyen",
  AdminIssued: "Dhuruar nga ne",
  AdminRevoked: "Hequr nga ne",
  SpentOnTravelReservation: "Pagoi një biletë",
  ServiceRecovery: "Kompensim për një përvojë të keqe"
};

// The ledger's EntityName is the database table a movement points at. Printing it raw put
// "JourneyReservation #1714" and "UserDailyChallenge #455" in front of support agents, who should not need
// to know our table names to read a customer's history.
export const ENTITY_NAME_LABELS: Record<string, string> = {
  JourneyReservation: "Biletë",
  Ticket: "Biletë",
  // Flex upgrades, ndryshim i datës, diferenca e kthimit të hapur — gjithçka e blerë mbi një
  // biletë ekzistuese. Pa këtë rreshti dilte me emrin e tabelës.
  ApplicationForReservationAddition: "Shtesë e biletës",
  EsimReservation: "Paketë eSIM",
  UserDailyChallenge: "Sfidë ditore",
  StreakMilestone: "Seri ditore",
  LeaderboardWeek: "Renditje javore",
  VoucherCode: "Kupon",
  GameSession: "Lojë",
  GameContinue: "Vazhdim i lojës",
  ScratchCard: "Fshi dhe fito",
  StreakRestore: "Rikthim i serisë",
  StreakFreeze: "Ngrirje e serisë",
  TestTopUp: "Shtim provë",
  // A per-lot expiry row points at another ledger row, which tells an agent nothing. The "Veprimi"
  // column already says the credits lapsed, so name the thing rather than repeating the event.
  WalletCredit: "Kredite të vjetra"
};

// Falls back to the raw name rather than hiding an unmapped value — a blank cell would be worse than
// a developer-looking one, and it surfaces any new enum member that needs a translation.
export const labelFor = (map: Record<string, string>, key: string | null) =>
  (key && map[key]) || key || "-";

export interface IWalletSummary {
  walletId: number;
  email: string;
  total: number;
  spendableOnEsim: number;
  spendableOnTravel: number;
  nextExpiryAtUtc: string | null;
  lastTransactionAtUtc: string | null;
}

/**
 * The three figures. Deliberately NOT a partition: a flexible lot counts toward both products, so
 * spendableOnEsim + spendableOnTravel exceeds total whenever any flexible credits exist. Never render
 * these as slices of one pie.
 */
export interface ICreditBalanceBreakdown {
  total: number;
  spendableOnEsim: number;
  spendableOnTravel: number;
  flexibleAny: number;
  esimOnly: number;
  travelOnly: number;
  nextExpiryAtUtc: string | null;
  /** Only the value sharing that timestamp — not the whole balance. */
  nextExpiryAmount: number;
}

export interface ICreditLot {
  creditId: number;
  type: string;
  scope: CreditRedeemableScope;
  remaining: number;
  expiresAtUtc: string | null;
  grantedAt: string;
}

export interface IWalletLedgerEntry {
  id: number;
  date: string;
  type: string;
  amount: number;
  redeemableScope: string;
  product: string;
  entityName: string | null;
  entityId: number | null;
  expiresAtUtc: string | null;
  note: string | null;
  issuedByAdminId: number | null;
  /** Optional — the ledger falls back to the id until the API resolves the name. */
  issuedByAdminName?: string | null;
}

export interface IWalletDetail {
  summary: IWalletSummary;
  breakdown: ICreditBalanceBreakdown;
  openLots: ICreditLot[];
  /**
   * Debits beyond every grant. Non-zero means the ledger is overdrawn (rows written before
   * concurrent spending was guarded); the balance is floored at zero, so this page is the only
   * place the floor's work is visible.
   */
  overdrawn: number;
}

export interface IIssueCreditsRequest {
  email: string;
  amount: number;
  redeemableScope: CreditRedeemableScope;
  /** Never expires, and returned in full on cancellation regardless of the refund tier. */
  isServiceRecovery: boolean;
  reason: string;
  expiresAtUtc?: string | null;
  /** Same value on every retry of one submitted form, so a retry cannot grant twice. */
  clientRequestId: string;
}

export interface IRevokeCreditsRequest {
  email: string;
  amount: number;
  redeemableScope: CreditRedeemableScope;
  reason: string;
}

/** How a booking was actually paid for. */
export interface IReservationPaymentBreakdown {
  reservationId: number;
  uniqueJourneyId: string;
  /** Gross value — what the operator is billed against, unaffected by credits. */
  totalPrice: number;
  paidByCash: number;
  paidByCredits: number;
  paymentMethod: string;
  walletId: number | null;
  creditMovements: IWalletLedgerEntry[];
}

export interface ICreditLiabilityReport {
  totalOutstanding: number;
  /**
   * Credits that replaced a refund the customer was owed, versus credits we gave away. Both are
   * liability and neither is convertible to money — the split exists because only the promotional
   * half is a marketing cost, and only it can be revoked without a second admin signing off.
   */
  fromRefundsOutstanding: number;
  promotionalOutstanding: number;
  spendableOnEsimOnly: number;
  spendableOnTravelOnly: number;
  flexible: number;
  byType: Record<string, number>;
  walletsWithBalance: number;
}

export interface ICreditActivityReport {
  fromUtc: string;
  toUtc: string;
  /** The cost, recognised when granted. */
  issued: number;
  /** A cash-reconciliation input, not a second cost. */
  redeemed: number;
  /** Breakage — credited back to the P&L in the period it lapses. */
  expired: number;
  returned: number;
  issuedByType: Record<string, number>;
  redeemedByProduct: Record<string, number>;
  /** One entry per CET day of the range; absent on an API that predates it. */
  daily?: ICreditActivityDay[];
}

export interface ICreditActivityDay {
  date: string;
  issued: number;
  redeemed: number;
  expired: number;
  returned: number;
}
